import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import type { MeResponseDto } from '@shared/api-dto';
import { HuacaClient, type AuthStateDto } from '../api/client';

/**
 * Where the app should be, derived from the server rather than guessed.
 *
 * `select-org` is a real state, not an edge case: /auth/refresh returns 200 with a NULL accessToken
 * when the user belongs to several orgs and none is selected, so a client that assumes "200 ⇒ logged
 * in" spins forever. (A new user's org is auto-created at sign-in, so there is no `needs-org` phase; a
 * degraded `needsOrg:true` — auto-create failed — carries a null token and falls through to
 * `anonymous`, i.e. the login screen, where re-login retries the create.)
 */
export type SessionPhase = 'loading' | 'anonymous' | 'select-org' | 'ready';

export interface SessionState {
  phase: SessionPhase;
  auth: AuthStateDto | null;
  me: MeResponseDto | null;
  error: string | null;
}

export interface AuthContextValue extends SessionState {
  client: HuacaClient;
  /** Re-read /auth/me — call after anything that changes entitlement (trial, seat, role). */
  reload: () => Promise<void>;
  selectOrg: (orgId: string) => Promise<void>;
  logout: () => Promise<void>;
  /** Permission check against the SERVER's resolved permissions (supports `*` wildcards). */
  can: (permission: string) => boolean;
  /** Does the caller hold a seat on this product? (Module roles are inert without one.) */
  isSeated: (productId: string) => boolean;
}

const AuthContext = createContext<AuthContextValue | null>(null);

/** Mirrors the server's segment-wise wildcard matching (platform/config/permissions.ts). */
function permissionSatisfies(granted: string[], required: string): boolean {
  const req = required.split(':');
  return granted.some((g) => {
    const parts = g.split(':');
    return parts.length === req.length && parts.every((p, i) => p === '*' || p === req[i]);
  });
}

export interface AuthProviderProps {
  apiBase: string;
  /** This module's origin — logins return here, not to a central app. */
  webOrigin?: string;
  children: ReactNode;
}

export function AuthProvider({ apiBase, webOrigin, children }: AuthProviderProps) {
  const clientRef = useRef<HuacaClient | null>(null);
  clientRef.current ??= new HuacaClient({ apiBase, ...(webOrigin ? { webOrigin } : {}) });
  const client = clientRef.current;

  const [state, setState] = useState<SessionState>({ phase: 'loading', auth: null, me: null, error: null });

  const applyAuth = useCallback(
    async (auth: AuthStateDto): Promise<void> => {
      // needsSelection ⇒ several orgs, pick one. A degraded needsOrg (auto-create failed) also carries
      // a null token, so it falls through here to `anonymous` → the login screen; re-login retries.
      if (auth.needsSelection) return setState({ phase: 'select-org', auth, me: null, error: null });
      if (!auth.accessToken) return setState({ phase: 'anonymous', auth, me: null, error: null });
      const me = await client.me();
      setState({ phase: 'ready', auth, me, error: null });
    },
    [client],
  );

  /** Boot: the refresh cookie is HttpOnly, so the ONLY way to know if we're logged in is to ask. */
  const boot = useCallback(async () => {
    try {
      await applyAuth(await client.refresh());
    } catch {
      setState({ phase: 'anonymous', auth: null, me: null, error: null });
    }
  }, [applyAuth, client]);

  useEffect(() => {
    void boot();
  }, [boot]);

  const value = useMemo<AuthContextValue>(
    () => ({
      ...state,
      client,
      reload: async () => {
        // Re-read from the server; entitlement changes (trial started, seat granted) show up here.
        try {
          const me = await client.me();
          setState((s) => ({ ...s, phase: 'ready', me }));
        } catch {
          await boot();
        }
      },
      selectOrg: async (orgId) => {
        await client.selectOrg(orgId);
        setState({ phase: 'ready', auth: state.auth, me: await client.me(), error: null });
      },
      logout: async () => {
        await client.logout();
        setState({ phase: 'anonymous', auth: null, me: null, error: null });
      },
      can: (permission) => permissionSatisfies(state.me?.permissions ?? [], permission),
      isSeated: (productId) => (state.me?.seatedModules ?? []).includes(productId),
    }),
    // `applyAuth` is deliberately absent: the memo body does not call it. Listing it re-created the
    // context value (re-rendering every consumer) each time its identity changed, for nothing.
    [state, client, boot],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useSession(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useSession must be used inside <AuthProvider>');
  return ctx;
}
